/*
  Per-user authentication for the odds screen.

  Replaces the shared account whose credentials were compiled into the public
  bundle. See docs/kibl-screen-authentication.md.

  The user's password is NEVER persisted. Login exchanges it once for tokens;
  from then on the access token is renewed with the refresh token
  (REFRESH_TOKEN_AUTH, 30-day validity on this app client). The password lives
  only in the login form's React state and is discarded when the component
  unmounts.

  Session state is written to the same `cognito_state` key KiblCognitoService
  already reads, so the running app picks it up with no changes to how it asks
  for a token.
*/

const COGNITO_URL = process.env.REACT_APP_KIBL_ODDS_SERVER_COGNITO_AUTH_URL;
const CLIENT_ID = process.env.REACT_APP_KIBL_ODDS_SERVER_COGNITO_USER_APP_CLIENT_ID;

export const SESSION_KEY = 'cognito_state';

function cognitoRequest(body) {
  return fetch(COGNITO_URL, {
    method: 'POST',
    headers: new Headers({
      accept: 'application/json',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      'Content-Type': 'application/x-amz-json-1.1',
    }),
    body: JSON.stringify(body),
  });
}

export function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    // Corrupt or unreadable — treat as signed out rather than crashing the
    // bootstrap, which happens before React renders anything.
    return null;
  }
}

function writeSession(session) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (_) {
    // Private browsing / quota. The session still works for this page load;
    // the user is asked to sign in again on the next one.
  }
}

export function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch (_) {}
}

function sessionFromAuthResult(username, result, previousRefreshToken) {
  return {
    username,
    // password is deliberately absent — see the note at the top of this file.
    access_token: result.AccessToken,
    // A REFRESH_TOKEN_AUTH response does not return a new refresh token, so
    // carry the existing one forward or it is lost on the first renewal.
    refresh_token: result.RefreshToken || previousRefreshToken || '',
    expires_in: new Date().getTime() + (parseInt(result.ExpiresIn, 10) - 3) * 1000,
    force_generate: false,
  };
}

export async function signIn(username, password) {
  const response = await cognitoRequest({
    AuthParameters: { USERNAME: username, PASSWORD: password },
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: CLIENT_ID,
  });

  const data = await response.json();

  if (!response.ok || !data.AuthenticationResult) {
    // Cognito distinguishes these, but the distinction is only useful to
    // someone guessing at accounts, so both surface as one message.
    const type = String(data.__type || '');
    if (type.includes('NotAuthorized') || type.includes('UserNotFound')) {
      throw new Error('Incorrect username or password.');
    }
    if (type.includes('PasswordResetRequired')) {
      throw new Error('This account needs its password reset. Please get in touch.');
    }
    if (type.includes('TooManyRequests') || type.includes('LimitExceeded')) {
      throw new Error('Too many attempts. Please wait a moment and try again.');
    }
    throw new Error(data.message || 'Unable to sign in. Please try again.');
  }

  const session = sessionFromAuthResult(username, data.AuthenticationResult, null);
  writeSession(session);
  return session;
}

/*
  Renew the access token without the password. Returns the refreshed session,
  or null when the refresh token has expired or been revoked — in which case
  the caller should send the user back to the login screen.
*/
export async function refreshSession(session) {
  if (!session || !session.refresh_token) return null;

  try {
    const response = await cognitoRequest({
      AuthParameters: { REFRESH_TOKEN: session.refresh_token },
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: CLIENT_ID,
    });

    const data = await response.json();
    if (!response.ok || !data.AuthenticationResult) return null;

    const renewed = sessionFromAuthResult(
      session.username,
      data.AuthenticationResult,
      session.refresh_token
    );
    writeSession(renewed);
    return renewed;
  } catch (_) {
    // Offline or Cognito unreachable. Null sends the user to the login screen,
    // which is the honest outcome — we cannot prove the session is still valid.
    return null;
  }
}

/*
  Called once at startup, before App is imported. Resolves to a usable session
  or null.

  A session with a live access token is used as-is. An expired one is renewed
  from the refresh token, so a returning user is not asked to sign in again
  until the refresh token itself expires.
*/
export async function resolveSession() {
  const session = readSession();
  if (!session || !session.access_token) return null;

  if (session.expires_in && new Date().getTime() < session.expires_in) {
    return session;
  }

  return await refreshSession(session);
}
